import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';
import type { RiassuntoScheda } from '../../lib/eventi/riepilogo';
import { SCHEDE, type SchedaEvento } from './schede';

type Props = {
  attiva: SchedaEvento;
  onCambia: (scheda: SchedaEvento) => void;
  riassunti: Record<SchedaEvento, RiassuntoScheda>;
};

/** Il colore della barretta: lo stesso dei pallini della scheda Richieste. */
const BARRETTA: Partial<Record<SchedaEvento, string>> = {
  biglietti: 'bg-green-500',
  richieste: 'bg-amber-400',
  assegnazioni: 'bg-green-500',
};

/**
 * Le schede di un evento come tappe: ognuna dice a che punto e' (quanti
 * hanno scelto, quanti sono confermati, cosa manca), cosi' si capisce dove
 * andare senza aprirle tutte.
 */
export function SchedeEvento({ attiva, onCambia, riassunti }: Props) {
  return (
    <nav aria-label="Schede dell'evento">
      <div role="tablist" className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3 lg:grid-cols-5">
        {SCHEDE.map(({ id, etichetta, icona: Icona }) => {
          const r = riassunti[id];
          const scelta = attiva === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={scelta}
              onClick={() => onCambia(id)}
              className={cn(
                'flex flex-col rounded-xl border p-3 text-left transition-all md:p-4',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                scelta
                  ? 'border-primary-300 bg-primary-50/60 shadow-sm ring-1 ring-primary-300'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm',
              )}
            >
              <span className="flex items-center gap-2.5">
                <span
                  className={cn(
                    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full',
                    r.fatto ? 'bg-green-100 text-green-700' : scelta ? 'bg-primary-600 text-white' : 'bg-gray-100 text-neutral-600',
                  )}
                >
                  {r.fatto ? <Check className="h-4 w-4" /> : <Icona className="h-3.5 w-3.5" />}
                </span>
                <span className={cn('text-sm font-semibold', scelta ? 'text-primary-800' : 'text-gray-900')}>{etichetta}</span>
              </span>
              <span className={cn('mt-2 block text-xs leading-snug', r.avviso ? 'font-medium text-amber-700' : 'text-neutral-500')}>
                {r.testo}
              </span>
              {r.avanzamento !== undefined && (
                <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-gray-100">
                  <span
                    className={cn('block h-full rounded-full', BARRETTA[id] ?? 'bg-neutral-400')}
                    style={{ width: `${Math.round(r.avanzamento * 100)}%` }}
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
